import { useEffect, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { AnimatePresence, motion } from 'motion/react'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { cn } from '@/lib/utils'
import { useTranslations } from '@/lib/i18n'
import type { SharedProps } from '@/types'

type Tone = 'notice' | 'alert'
interface Toast {
  id: number
  tone: Tone
  message: string
}

const TONES: Record<Tone, string> = {
  notice: 'bg-status-success-bg text-status-success-fg',
  alert: 'bg-status-failed-bg text-status-failed-fg',
}

const DISMISS_MS = 5000

/**
 * Surfaces Rails flash (notice/alert) from Inertia shared props as auto-
 * dismissing toasts. Mounted once in each layout so every action gets visible
 * feedback. Accessible: notices use role="status", alerts role="alert".
 */
export function FlashToaster() {
  const { props } = usePage<SharedProps>()
  const { t } = useTranslations()
  const { notice, alert } = props.flash
  const [toasts, setToasts] = useState<Toast[]>([])

  useEffect(() => {
    const incoming: Toast[] = []
    if (notice) incoming.push({ id: Date.now(), tone: 'notice', message: notice })
    if (alert) incoming.push({ id: Date.now() + 1, tone: 'alert', message: alert })
    if (incoming.length === 0) return

    setToasts((current) => [...current, ...incoming])
    const ids = incoming.map((t) => t.id)
    const timer = setTimeout(() => {
      setToasts((current) => current.filter((t) => !ids.includes(t.id)))
    }, DISMISS_MS)
    return () => clearTimeout(timer)
  }, [notice, alert])

  function dismiss(id: number) {
    setToasts((current) => current.filter((t) => t.id !== id))
  }

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2">
      <AnimatePresence initial={false}>
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            layout
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            role={toast.tone === 'alert' ? 'alert' : 'status'}
            className={cn(
              'pointer-events-auto flex items-start gap-3 rounded-control px-4 py-3 text-sm shadow-md',
              TONES[toast.tone],
            )}
          >
            <span className="flex-1">{toast.message}</span>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="shrink-0 opacity-70 transition hover:opacity-100"
              aria-label={t('common.dismiss')}
            >
              <XMarkIcon className="size-4" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
